import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { ClientSettings } from "src/model/class/client-settings";
import { Supervision } from "src/model/class/general classes/supervision";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { SupervisionPipe } from "src/pipes/supervision-name.pipe";
import { NetworkService } from "src/service/network.service";
import { SystemRouterService } from "src/service/system-router.service";
import { ThemeService } from "src/service/theme.service";
import { TranslationService } from "src/service/translation.service";
import { clientSettings } from "src/state/selectors/client-settings.selectors";

@Component({
    selector: "app-supervision",
    templateUrl: "./supervision.component.html",
    styleUrls: ["./supervision.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SupervisionComponent implements OnInit {
  //#region Subcriptions to "_store.select(...)" Observables
  private _selectClientSettingsUserSub: Subscription = null;
  private _translateSubscription: Subscription = null;
  private _networkServiceSubscription: Subscription = null;

  //#endregion

  private _currentClientSetting: ClientSettings = null;

  public name: string = "";
  public isOnline: boolean = false;

  @Input("") supervision: Supervision;

  constructor(
    private _systemRouterService: SystemRouterService,
    private _translationService: TranslationService,
    private _translateService: TranslateService,
    private _store: Store,
    private _networkService: NetworkService,
    private _supervisionPipe: SupervisionPipe,
    private _themeService: ThemeService
  ) { }

  ngOnInit() {
    this.subscribeNetworkServiceData();
    this.subscribeSettingServiceData();

    this.name = this._supervisionPipe.transform(this.supervision);
    this.subscribeTranslationEvent();
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
    if (this._translateSubscription) {
      this._translateSubscription.unsubscribe();
    }
    if (this._networkServiceSubscription) {
      this._networkServiceSubscription.unsubscribe();
    }
  }

  /**
   * Redirect to single supervision graphical-type based on toggle value
   *
   * @param idSupervision ID custome interface to open
   * @param idEnvironment ID releated custom interface
   */
  public onClickGoToSingleSupervision(
    idSupervision: number,
    idEnvironment: number
  ): void {
    if (this._currentClientSetting) {
      if (this._currentClientSetting.supervisionListViewMode) {
        this._systemRouterService.navigateToSupervisionList(
          idSupervision,
          idEnvironment
        );
      } else {
        this._systemRouterService.navigateToSupervisionGraph(
          idSupervision,
          idEnvironment
        );
      }
    }
  }

  /**
   * Subscribes to the translation event to properly load the name of supervision depending-
   * depending on whether it's Digital place or not
   */
  private subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.name = this._supervisionPipe.transform(this.supervision);
      }
    );
  }

  /**
   * Subsribe to public Observable "clientSettings" of "settings.service", which propagates the info about the general app
   * -related settings as defined for this device; it will also emit each time these settings are changed.
   */
  private subscribeSettingServiceData(): void {
    this._selectClientSettingsUserSub = this._store
      .select(clientSettings)
      .pipe(filter((clientSetting) => clientSetting !== null))
      .subscribe((clientSettings: ClientSettings) => {
        this._currentClientSetting = clientSettings;
      });
  }

  /**
   * Handle subscribtion to network service
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.isOnline = !(currentConnectionType === ConnectionType.NONE);
        }
      );
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectClientSettingsUserSub) {
      this._selectClientSettingsUserSub.unsubscribe();
    }
  }
  //#endregion
}
